import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { SchedulingRouteParams } from "types/schedulingRouteParams";
import { cloneDeep, isEmpty } from "lodash";

declare let schedulerDatalayerMode: string;

const hasRequiredAppointmentCreatedFields = (
  eventName: string,
  eventProperties?: Record<string, unknown>
): boolean => {
  if (eventName !== "step-8-appointment-created") {
    return true;
  }

  return !!eventProperties?.appointmentId && !!eventProperties?.patientId;
};

export const getNewEventName = (eventName: string): string => {
  switch (eventName) {
    case "step-1-scheduler-enter":
      return "step-1";
    case "step-5-selected-appointment-time":
      return "step-2";
    case "step-3-selected-eyewear-type":
      return "step-3";
    case "step-4-selected-patient-type":
      return "step-4";
    case "step-7-account-created":
      return "step-5";
    case "step-8-appointment-created":
      return "step-6";
    default:
      return eventName;
  }
};

export const getNewKey = (oldKey: string): string => {
  switch (oldKey) {
    case "logicalId":
      return "OID";
    case "location":
      return "LID";
    case "office_type":
      return "OFT";
    case "total_slots":
      return "TTLST";
    case "veh_slots":
      return "VEHST";
    case "traditional_slots":
      return "TDLST";
    case "slot_type":
      return "SLTT";
    case "doctorId":
    case "doctorID":
      return "DID";
    case "selected_type":
      return "STT";
    case "patient_type":
      return "PT";
    case "appointmentId":
      return "AID";
    case "totalSlotCount":
      return "TTLSTC";
    case "hiddenSlotCount":
      return "HSTC";
    case "visibleSlotCount":
      return "VSTC";
    case "Traditional":
      return "TDL";
    case "glasses_no_try_contacts":
      return "GNTC";
    case "glasses_try_contacts":
      return "GTC";
    case "contacts":
      return "C";
    case "glasses_and_contacts":
      return "GC";
    case "no_glasses_or_contacts":
      return "NGC";
    case "returning-patient":
      return "RP";
    case "new-patient":
      return "NP";
    case "patient":
      return "P";
    case "patientId":
      return "PID";
    default:
      return oldKey;
  }
};

export const useTrackEvent = (): {
  trackEvent: (
    eventName: string,
    eventProperties?: Record<string, unknown>
  ) => void;
} => {
  
  const { officeId, examType, patientReturnStatus } =
    useParams<SchedulingRouteParams>();

  const trackEvent = useCallback(
    (eventName, eventProperties) => {
      if (!hasRequiredAppointmentCreatedFields(eventName, eventProperties)) {
        return;
      }

      if( schedulerDatalayerMode && (schedulerDatalayerMode === "legacy" || schedulerDatalayerMode === "dual")) {
        const event = {
          event: eventName,
          ...(officeId ? { location: officeId } : {}),
          ...(examType ? { selected_type: examType } : {}),
          ...(patientReturnStatus ? { patient_type: patientReturnStatus } : {}),
          ...(eventProperties ?? {}),
        };
        console.log("Tracking event:", event);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(event);
      }

      if( schedulerDatalayerMode && (schedulerDatalayerMode === "dual" || schedulerDatalayerMode === "new")) {
        let newEventName = getNewEventName(eventName);
        let newEventProperties = cloneDeep(eventProperties);
        if(!isEmpty(newEventProperties)){
          Object.keys(newEventProperties).forEach((key) => {
            if (key === "patientSmsOptIn" || key === "patientPhoneNumber") {
              delete newEventProperties[key];
              return;
            } 
            let keyCompare = getNewKey(key);
            if (keyCompare !== key) {
              newEventProperties[keyCompare] = getNewKey(newEventProperties[key]);
              delete newEventProperties[key];
            }
          });
        }        
        const newEvent = {
          event: newEventName,
          ...(newEventProperties ?? {}),
        };
        console.log("Tracking new event:", newEvent);
        window.dataLayer.push(newEvent);
      }

    },
    [officeId, examType, patientReturnStatus]
  );

  return { trackEvent };
};
